import type {
  AnnouncementCampaignFunnel,
  AnnouncementCampaignRecipientStatus,
  AnnouncementEngagementFilter,
} from "./types";
import { ANNOUNCEMENT_RESEND_TAG } from "./announcement-campaign";

export const ANNOUNCEMENT_ENGAGEMENT_FILTERS = [
  "any",
  "not_opened",
  "not_clicked",
  "not_created_party",
] as const satisfies readonly AnnouncementEngagementFilter[];

export const ANNOUNCEMENT_UTM_SOURCE = "announcement";
export const ANNOUNCEMENT_UTM_MEDIUM = "email";
export const ANNOUNCEMENT_CAMPAIGN_COOKIE = "ap_announcement_campaign";
export const ANNOUNCEMENT_CAMPAIGN_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

export const ANNOUNCEMENT_TRACKED_HOSTS = [
  "awardparty.app",
  "www.awardparty.app",
  "localhost",
  "127.0.0.1",
] as const;

const ANNOUNCEMENT_CAMPAIGN_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isAnnouncementEngagementFilter(
  value: string,
): value is AnnouncementEngagementFilter {
  return (ANNOUNCEMENT_ENGAGEMENT_FILTERS as readonly string[]).includes(
    value,
  );
}

export function parseAnnouncementEngagementFilter(
  value: unknown,
): AnnouncementEngagementFilter {
  if (typeof value === "string" && isAnnouncementEngagementFilter(value)) {
    return value;
  }
  return "any";
}

export function parseIncludeNotOnPriorCampaign(value: unknown): boolean {
  return value === true || value === "true" || value === 1 || value === "1";
}

export function parseAnnouncementCampaignId(
  value: unknown,
): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  if (!ANNOUNCEMENT_CAMPAIGN_ID_RE.test(trimmed)) {
    return null;
  }
  return trimmed.toLowerCase();
}

export function announcementCampaignIdFromSearchParams(params: {
  get(name: string): string | null;
}): string | null {
  const source = params.get("utm_source")?.trim().toLowerCase();
  if (source !== ANNOUNCEMENT_UTM_SOURCE) {
    return null;
  }
  return parseAnnouncementCampaignId(params.get("utm_campaign"));
}

export function announcementCampaignIdFromUrl(
  url: Pick<URL, "searchParams">,
): string | null {
  return announcementCampaignIdFromSearchParams(url.searchParams);
}

export function trackedHostFromOrigin(origin: string | null | undefined): string | null {
  if (!origin) {
    return null;
  }
  try {
    const hostname = new URL(origin).hostname.trim().toLowerCase();
    return hostname || null;
  } catch {
    return null;
  }
}

function hostnameIsTracked(
  hostname: string,
  trackedHosts: readonly string[],
): boolean {
  const host = hostname.trim().toLowerCase();
  return trackedHosts.some((candidate) => candidate.toLowerCase() === host);
}

function isUnsubscribePath(pathname: string): boolean {
  return (
    pathname === "/unsubscribe" ||
    pathname.startsWith("/unsubscribe/") ||
    pathname === "/api/unsubscribe" ||
    pathname.startsWith("/api/unsubscribe/")
  );
}

export function appendAnnouncementTrackingParams(
  href: string,
  campaignId: string,
  options: { trackedHosts?: readonly string[] } = {},
): string {
  const id = parseAnnouncementCampaignId(campaignId);
  if (!id) {
    return href;
  }

  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return href;
  }
  if (isUnsubscribePath(url.pathname)) {
    return href;
  }

  const trackedHosts = options.trackedHosts ?? ANNOUNCEMENT_TRACKED_HOSTS;
  if (!hostnameIsTracked(url.hostname, trackedHosts)) {
    return href;
  }

  url.searchParams.set("utm_source", ANNOUNCEMENT_UTM_SOURCE);
  url.searchParams.set("utm_medium", ANNOUNCEMENT_UTM_MEDIUM);
  url.searchParams.set("utm_campaign", id);
  return url.toString();
}

export function announcementResendTags(
  campaignId?: string | null,
): Array<{ name: string; value: string }> {
  const tags = [{ name: "category", value: ANNOUNCEMENT_RESEND_TAG }];
  const id = parseAnnouncementCampaignId(campaignId);
  if (id) {
    tags.push({ name: "campaign_id", value: id });
  }
  return tags;
}

export type AnnouncementResendEngagementType =
  | "email.opened"
  | "email.clicked";

export interface ParsedAnnouncementResendWebhook {
  type: AnnouncementResendEngagementType;
  emailId: string;
  occurredAt: Date;
  clickedUrl: string | null;
}

function readString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function readDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value !== "string") {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function parseAnnouncementResendWebhookPayload(
  payload: unknown,
): ParsedAnnouncementResendWebhook | null {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const envelope = payload as {
    type?: unknown;
    created_at?: unknown;
    createdAt?: unknown;
    data?: unknown;
  };

  const type =
    envelope.type === "email.opened" || envelope.type === "email.clicked"
      ? envelope.type
      : null;
  if (!type) {
    return null;
  }

  const data =
    envelope.data && typeof envelope.data === "object"
      ? (envelope.data as {
          email_id?: unknown;
          emailId?: unknown;
          created_at?: unknown;
          createdAt?: unknown;
          click?: { link?: unknown; timestamp?: unknown };
        })
      : null;
  if (!data) {
    return null;
  }

  const emailId = readString(data.email_id) ?? readString(data.emailId);
  if (!emailId) {
    return null;
  }

  const clickedUrl =
    type === "email.clicked" ? readString(data.click?.link) : null;
  const occurredAt =
    (type === "email.clicked" ? readDate(data.click?.timestamp) : null) ??
    readDate(envelope.created_at) ??
    readDate(envelope.createdAt) ??
    readDate(data.created_at) ??
    readDate(data.createdAt) ??
    new Date();

  return {
    type,
    emailId,
    occurredAt,
    clickedUrl,
  };
}

export interface AnnouncementEngagementTimestamps {
  openedAt: Date | string | null;
  clickedAt: Date | string | null;
  clickedUrl: string | null;
}

function hasTimestamp(value: Date | string | null | undefined): boolean {
  if (value == null) {
    return false;
  }
  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }
  return value.trim().length > 0;
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value !== "string" || value.trim().length === 0) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function applyAnnouncementEngagementEvent(
  current: AnnouncementEngagementTimestamps,
  event: {
    type: AnnouncementResendEngagementType;
    occurredAt: Date;
    clickedUrl?: string | null;
  },
): {
  openedAt: Date;
  clickedAt: Date | null;
  clickedUrl: string | null;
  changed: boolean;
} | null {
  const openedAt = toDate(current.openedAt);
  const clickedAt = toDate(current.clickedAt);
  const clickedUrl = current.clickedUrl?.trim() || null;

  let nextOpenedAt = openedAt;
  let nextClickedAt = clickedAt;
  let nextClickedUrl = clickedUrl;

  if (event.type === "email.opened") {
    if (!nextOpenedAt) {
      nextOpenedAt = event.occurredAt;
    }
  } else {
    if (!nextClickedAt) {
      nextClickedAt = event.occurredAt;
    }
    if (!nextClickedUrl && event.clickedUrl?.trim()) {
      nextClickedUrl = event.clickedUrl.trim();
    }
    if (!nextOpenedAt) {
      nextOpenedAt = event.occurredAt;
    }
  }

  if (!nextOpenedAt) {
    return null;
  }

  const changed =
    openedAt?.getTime() !== nextOpenedAt.getTime() ||
    (clickedAt?.getTime() ?? null) !== (nextClickedAt?.getTime() ?? null) ||
    clickedUrl !== nextClickedUrl;

  if (!changed) {
    return null;
  }

  return {
    openedAt: nextOpenedAt,
    clickedAt: nextClickedAt,
    clickedUrl: nextClickedUrl,
    changed: true,
  };
}

export function engagementPatchFromResendLastEvent(
  lastEvent: string | null | undefined,
  occurredAt: Date = new Date(),
): { openedAt: Date | null; clickedAt: Date | null } {
  const event = lastEvent?.trim().toLowerCase();
  if (event === "clicked") {
    return { openedAt: occurredAt, clickedAt: occurredAt };
  }
  if (event === "opened") {
    return { openedAt: occurredAt, clickedAt: null };
  }
  return { openedAt: null, clickedAt: null };
}

export function mergeResendLastEventPatch(
  current: AnnouncementEngagementTimestamps,
  lastEvent: string | null | undefined,
  occurredAt: Date = new Date(),
): {
  openedAt: Date | null;
  clickedAt: Date | null;
  changed: boolean;
} {
  const patch = engagementPatchFromResendLastEvent(lastEvent, occurredAt);
  const openedAt = toDate(current.openedAt) ?? patch.openedAt;
  const clickedAt = toDate(current.clickedAt) ?? patch.clickedAt;

  const changed =
    (!hasTimestamp(current.openedAt) && patch.openedAt != null) ||
    (!hasTimestamp(current.clickedAt) && patch.clickedAt != null);

  return { openedAt, clickedAt, changed };
}

export interface PriorCampaignRecipientEngagement {
  userId: string;
  status: AnnouncementCampaignRecipientStatus;
  openedAt?: Date | string | null;
  clickedAt?: Date | string | null;
  partyCreatedAt?: Date | string | null;
}

export function sentUserIdsFromPriorCampaign(
  recipients: readonly PriorCampaignRecipientEngagement[],
): Set<string> {
  const ids = new Set<string>();
  for (const row of recipients) {
    if (row.status === "sent") {
      ids.add(row.userId);
    }
  }
  return ids;
}

export function sentUserIdsWithoutEngagement(
  recipients: readonly PriorCampaignRecipientEngagement[],
  filter: AnnouncementEngagementFilter,
): Set<string> {
  const ids = new Set<string>();
  if (filter === "any") {
    return ids;
  }

  for (const row of recipients) {
    if (row.status !== "sent") {
      continue;
    }
    if (filter === "not_opened" && !hasTimestamp(row.openedAt ?? null)) {
      ids.add(row.userId);
    }
    if (filter === "not_clicked" && !hasTimestamp(row.clickedAt ?? null)) {
      ids.add(row.userId);
    }
    if (
      filter === "not_created_party" &&
      !hasTimestamp(row.partyCreatedAt ?? null)
    ) {
      ids.add(row.userId);
    }
  }
  return ids;
}

export function recipientPassesEngagementFilter(options: {
  userId: string;
  filter: AnnouncementEngagementFilter;
  priorCampaignId?: string | null;
  sentUserIds: ReadonlySet<string>;
  sentWithoutEngagement: ReadonlySet<string>;
  includeNotOnPriorCampaign: boolean;
}): boolean {
  if (options.filter === "any" || !options.priorCampaignId) {
    return true;
  }
  if (options.sentWithoutEngagement.has(options.userId)) {
    return true;
  }
  if (options.sentUserIds.has(options.userId)) {
    return false;
  }
  return options.includeNotOnPriorCampaign;
}

export function summarizeAnnouncementFunnel(
  rows: Array<{
    status: string;
    openedAt?: Date | string | null;
    clickedAt?: Date | string | null;
    partyCreatedAt?: Date | string | null;
  }>,
): AnnouncementCampaignFunnel {
  let sentCount = 0;
  let openedCount = 0;
  let clickedCount = 0;
  let partiesCreatedCount = 0;

  for (const row of rows) {
    if (row.status !== "sent") {
      continue;
    }
    sentCount += 1;
    if (hasTimestamp(row.openedAt ?? null)) {
      openedCount += 1;
    }
    if (hasTimestamp(row.clickedAt ?? null)) {
      clickedCount += 1;
    }
    if (hasTimestamp(row.partyCreatedAt ?? null)) {
      partiesCreatedCount += 1;
    }
  }

  return {
    sentCount,
    openedCount,
    clickedCount,
    partiesCreatedCount,
  };
}

export function announcementFunnelPercent(
  part: number,
  sentCount: number,
): number {
  if (sentCount <= 0) {
    return 0;
  }
  return Math.round((part / sentCount) * 100);
}

export function announcementPartyConversionUpdate(
  current: {
    partyCreatedAt: Date | string | null;
    createdPartyId: string | null;
  },
  input: { partyId: string; now?: Date },
): { partyCreatedAt: Date; createdPartyId: string } | null {
  if (hasTimestamp(current.partyCreatedAt)) {
    return null;
  }
  const partyId = input.partyId.trim();
  if (!partyId) {
    return null;
  }
  return {
    partyCreatedAt: input.now ?? new Date(),
    createdPartyId: partyId,
  };
}
